import { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Globe, Send, CheckCircle, MessageCircle, Navigation } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

const courses = [
  'MS-CIT',
  'DTP',
  'Java',
  'Tally Prime with GST',
  'Advanced Tally',
  'Advanced Excel',
  'Computer Typing',
  'C & C++',
  'Python',
  'Web Designing',
  'Digital Marketing',
  'ABACUS',
];

export default function Contact() {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    course: '',
    batch: '',
    message: '',
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Simulate form submission
    setIsSubmitted(true);
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({ name: '', mobile: '', course: '', batch: '', message: '' });
    }, 3000);
  };

  return (
    <section id="contact" className="section-padding bg-gradient-to-b from-cp-blue-dark to-cp-blue relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 -left-32 w-96 h-96 bg-cp-orange/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
      </div>

      <div className="container-custom relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            {t('contact.title')}
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            {t('contact.subtitle')}
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="glass rounded-2xl p-8 h-full">
              <h3 className="text-2xl font-bold text-white mb-6">{t('contact.info')}</h3>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-cp-orange/20 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-cp-orange" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-1">{t('contact.address')}</h4>
                    <p className="text-gray-400 text-sm leading-relaxed">
                      Opp. Babde Sabhagrūha, Behind Jaiparkar Lawn,<br />
                      Mahajanwadi, Hingna Road, Wanadongari, Nagpur
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-cp-orange/20 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-cp-orange" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-1">{t('contact.phone')}</h4>
                    <div className="space-y-1">
                      <a href="tel:8007808098" className="text-gray-400 text-sm hover:text-cp-orange transition-colors block">8007808098</a>
                      <a href="tel:7709155161" className="text-gray-400 text-sm hover:text-cp-orange transition-colors block">7709155161</a>
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-cp-orange/20 flex items-center justify-center flex-shrink-0">
                    <Globe className="w-6 h-6 text-cp-orange" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-1">{t('contact.website')}</h4>
                    <a href="https://CPCE.co.in" target="_blank" rel="noopener noreferrer" className="text-gray-400 text-sm hover:text-cp-orange transition-colors">
                      CPCE.co.in
                    </a>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="grid grid-cols-2 gap-4 mt-8">
                <a
                  href="tel:8007808098"
                  className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-green-600 hover:bg-green-700 text-white font-medium transition-colors"
                >
                  <Phone className="w-5 h-5" />
                  {t('contact.call')}
                </a>
                <a
                  href="https://wa.me/918007808098"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-green-500 hover:bg-green-600 text-white font-medium transition-colors"
                >
                  <MessageCircle className="w-5 h-5" />
                  {t('contact.whatsapp')}
                </a>
                <a
                  href="https://maps.google.com/?q=Career+Point+Computer+Education+Nagpur"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-medium transition-colors"
                >
                  <Navigation className="w-5 h-5" />
                  {t('contact.directions')}
                </a>
                <a
                  href="#contact"
                  className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl btn-primary text-white font-medium"
                >
                  <Send className="w-5 h-5" />
                  {t('contact.enquiry')}
                </a>
              </div>
            </div>
          </motion.div>

          {/* Admission Form */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="glass rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-white mb-6">{t('form.title')}</h3>
              
              {isSubmitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex flex-col items-center justify-center py-12 text-center"
                >
                  <CheckCircle className="w-16 h-16 text-green-500 mb-4" />
                  <h4 className="text-xl font-bold text-white mb-2">{t('form.success')}</h4>
                  <p className="text-gray-400">{t('form.successDesc')}</p>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">{t('form.name')}</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-cp-orange focus:ring-1 focus:ring-cp-orange transition-colors"
                      placeholder={t('form.namePlaceholder')}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">{t('form.mobile')}</label>
                    <input
                      type="tel"
                      name="mobile"
                      value={formData.mobile}
                      onChange={handleChange}
                      required
                      pattern="[0-9]{10}"
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-cp-orange focus:ring-1 focus:ring-cp-orange transition-colors"
                      placeholder={t('form.mobilePlaceholder')}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">{t('form.course')}</label>
                    <select
                      name="course"
                      value={formData.course}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:outline-none focus:border-cp-orange focus:ring-1 focus:ring-cp-orange transition-colors appearance-none cursor-pointer"
                    >
                      <option value="" className="bg-cp-blue-dark">{t('form.selectCourse')}</option>
                      {courses.map((course) => (
                        <option key={course} value={course} className="bg-cp-blue-dark">
                          {course}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">{t('form.batch')}</label>
                    <select
                      name="batch"
                      value={formData.batch}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:outline-none focus:border-cp-orange focus:ring-1 focus:ring-cp-orange transition-colors appearance-none cursor-pointer"
                    >
                      <option value="" className="bg-cp-blue-dark">{t('form.selectBatch')}</option>
                      <option value="morning" className="bg-cp-blue-dark">{t('form.morning')}</option>
                      <option value="afternoon" className="bg-cp-blue-dark">{t('form.afternoon')}</option>
                      <option value="evening" className="bg-cp-blue-dark">{t('form.evening')}</option>
                      <option value="weekend" className="bg-cp-blue-dark">{t('form.weekend')}</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">{t('form.message')}</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={4}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-cp-orange focus:ring-1 focus:ring-cp-orange transition-colors resize-none"
                      placeholder={t('form.messagePlaceholder')}
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full btn-primary py-4 rounded-xl font-semibold text-white flex items-center justify-center gap-2"
                  >
                    <Send className="w-5 h-5" />
                    {t('form.submit')}
                  </button>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}